import React, { Component } from "react";
import { Link, NavLink } from "react-router-dom";

// CSS
import "../../styles/newDesign.scss";
import "./Footer.scss";
import "./FooterFull.scss";

import { t } from "../../Helper/i18n";
import { hasAbout, brandingOf } from "../../Helper/Branding";
import { homeBlocksOf } from "../../Helper/homeBlocks";
import { hasModule } from "../../Helper/modules";

// A phone number with only digits (leading zero dropped, Egypt country code assumed like the
// reference design's own wa.me link) -- wa.me needs a bare international-format number, no "+",
// spaces or a local leading 0.
function waNumber(mobile) {
  if (!mobile) return null;
  const digits = mobile.replace(/\D/g, "");
  if (!digits) return null;
  return digits.startsWith("20") ? digits : `20${digits.replace(/^0/, "")}`;
}

// Brand marks stay on FontAwesome, as on the contact page.
const SOCIAL = [
  { key: "facebookUrl", icon: "fab fa-facebook-f", label: "Facebook" },
  { key: "instagramUrl", icon: "fab fa-instagram", label: "Instagram" },
  { key: "linkedinUrl", icon: "fab fa-linkedin-in", label: "LinkedIn" },
  { key: "behanceUrl", icon: "fab fa-behance", label: "Behance" },
];

const safeUrl = (u) => (typeof u === "string" && /^https?:\/\//i.test(u.trim()) ? u.trim() : null);

/** The newer themes' footer: who the teacher is, the ways around the site, the ways to reach them, and the
 *  teacher's name once more, large and faint, as the page's last line. */
function FullFooter({ tenant, wa }) {
  const name = (tenant && tenant.name) || "";
  const about = tenant && tenant.description ? String(tenant.description) : "";
  const socials = SOCIAL.map((s) => ({ ...s, url: safeUrl(tenant && tenant[s.key]) })).filter((s) => s.url);
  const showCourses = hasModule(tenant, "COURSES");
  const showLive = hasModule(tenant, "MEETINGS");
  const toTop = () => window.scrollTo({ top: 0, behavior: "smooth" });
  return (
    <footer className="site-footer is-full">
      <div className="nd-wrap ff-grid">
        <div className="ff-brand">
          <Link to="/" className="footer-brand">
            {tenant && tenant.image ? <img className="footer-logo" src={tenant.image} alt={name} /> : <span className="ff-mark" aria-hidden="true">{name.trim().charAt(0)}</span>}
            <span className="footer-brand-name">{name}</span>
          </Link>
          {about && <p>{about}</p>}
          {socials.length > 0 && (
            <div className="ff-social">
              {socials.map((s) => (
                <a key={s.key} href={s.url} target="_blank" rel="noopener noreferrer" aria-label={s.label}><i className={s.icon}></i></a>
              ))}
            </div>
          )}
        </div>

        <nav className="ff-col" aria-label={t("ff.site")}>
          <b>{t("ff.site")}</b>
          <NavLink exact to="/">{t("home")}</NavLink>
          {hasAbout(tenant) && <NavLink to="/about">{t("about")}</NavLink>}
          {hasModule(tenant, "GALLERY") && <NavLink to="/gallery">{t("gallery")}</NavLink>}
          <NavLink to="/resources">{t("resources")}</NavLink>
          <NavLink to="/contact">{t("contact")}</NavLink>
        </nav>

        <nav className="ff-col" aria-label={t("ff.learn")}>
          <b>{t("ff.learn")}</b>
          {showCourses && <NavLink to="/courses">{t("courses")}</NavLink>}
          {showCourses && <NavLink to="/free-videos">{t("freeVideos")}</NavLink>}
          {showLive && <NavLink to="/live">{t("hb.liveEyebrow")}</NavLink>}
          <NavLink to="/sign-up">{t("hb.startFree")}</NavLink>
        </nav>

        <div className="ff-col ff-reach">
          <b>{t("ff.reach")}</b>
          {tenant && tenant.mobile && <a href={`tel:${tenant.mobile}`} dir="ltr"><i className="fas fa-phone"></i>{tenant.mobile}</a>}
          {wa && <a href={`https://wa.me/${wa}`} target="_blank" rel="noopener noreferrer"><i className="fab fa-whatsapp"></i>{t("whatsappLabel")}</a>}
          {tenant && tenant.email && <a href={`mailto:${tenant.email}`} dir="ltr"><i className="fas fa-envelope"></i>{tenant.email}</a>}
          {tenant && tenant.location && <span><i className="fas fa-map-marker-alt"></i>{tenant.location}</span>}
          {!(tenant && (tenant.mobile || tenant.email || tenant.location)) && <Link to="/contact"><i className="fas fa-comment-dots"></i>{t("contact")}</Link>}
        </div>
      </div>

      <div className="nd-wrap ff-bottom">
        <span>{t("rights", { year: new Date().getFullYear(), name })}</span>
        <button type="button" className="ff-top" onClick={toTop}>
          {t("ff.top")} <i className="fas fa-arrow-up" aria-hidden="true"></i>
        </button>
      </div>
      <div className="ff-giant" aria-hidden="true">{name}</div>
    </footer>
  );
}

class Footer extends Component {
  render() {
    const { tenant } = this.props;
    const teacherName = (tenant && tenant.name) || "";
    const wa = tenant && waNumber(tenant.mobile);
    // a theme with its own first screen is one of the newer, full-page themes: it takes the full footer
    const blocks = homeBlocksOf(brandingOf(tenant));
    const full = !!(blocks && blocks.indexOf("hero") >= 0);

    return (
      <div className="nd-page">
        {full ? (
          <FullFooter tenant={tenant} wa={wa} />
        ) : (
          <footer className="site-footer">
            <div className="nd-wrap footer-inner">
              <Link to="/" className="footer-brand">
                {tenant && tenant.image ? (
                  <img className="footer-logo" src={tenant.image} alt={teacherName} />
                ) : (
                  <span className="footer-brand-name">{teacherName}</span>
                )}
              </Link>
              <nav className="footer-nav">
                <NavLink exact to="/">{t("home")}</NavLink>
                <NavLink to="/courses">{t("courses")}</NavLink>
                <NavLink to="/gallery">{t("gallery")}</NavLink>
                {hasAbout(tenant) && <NavLink to="/about">{t("about")}</NavLink>}
                <NavLink to="/resources">{t("resources")}</NavLink>
                <NavLink to="/contact">{t("contact")}</NavLink>
              </nav>
              <div className="footer-copy">{t("rights", { year: new Date().getFullYear(), name: teacherName })}</div>
            </div>
          </footer>
        )}

        {wa && (
          <a
            href={`https://wa.me/${wa}`}
            target="_blank"
            rel="noopener noreferrer"
            className="whatsapp-float"
            aria-label={t("whatsapp")}
          >
            <i className="fab fa-whatsapp"></i>
          </a>
        )}
      </div>
    );
  }
}

export default Footer;
